'use client';

import { ExternalLink } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n/context';

const SubprocessorsPage = () => {
  const { t, isRTL } = useTranslation();

  // EU-based subprocessors for English (GDPR compliant)
  const euSubprocessors = [
    {
      name: 'AWS (EU-West)',
      purpose: t.features_pages['Cloud infrastructure and hosting services'],
      location: t.features_pages['Germany (Frankfurt)'],
      website: 'https://aws.amazon.com',
    },
    {
      name: 'Azure OpenAI (EU)',
      purpose: t.features_pages['AI language model services (GPT)'],
      location: t.features_pages['Sweden (Stockholm)'],
      website:
        'https://azure.microsoft.com/en-us/products/ai-services/openai-service',
    },
    {
      name: 'Stripe (EU)',
      purpose: t.features_pages['Payment processing and billing'],
      location: t.features_pages['Ireland (Dublin)'],
      website: 'https://stripe.com',
    },
    {
      name: 'Vercel (EU)',
      purpose: t.features_pages['Frontend hosting and deployment'],
      location: t.features_pages['Germany (Frankfurt)'],
      website: 'https://vercel.com',
    },
    {
      name: 'Cloudflare (EU)',
      purpose: t.features_pages['CDN and security services'],
      location: t.features_pages['Germany (Frankfurt)'],
      website: 'https://cloudflare.com',
    },
    {
      name: 'Neon (EU)',
      purpose: t.features_pages['Serverless PostgreSQL database'],
      location: t.features_pages['Germany (Frankfurt)'],
      website: 'https://neon.tech',
    },
    {
      name: 'PostHog (EU)',
      purpose: t.features_pages['Product analytics and event tracking'],
      location: t.features_pages['Germany (Frankfurt)'],
      website: 'https://posthog.com',
    },
    {
      name: 'Databuddy',
      purpose: t.features_pages['Website analytics'],
      location: t.features_pages['Finland'],
      website: 'https://databuddy.cc',
    },
  ];

  // GCC-based subprocessors for Arabic
  const gccSubprocessors = [
    {
      name: 'AWS (الشرق الأوسط)',
      purpose: t.features_pages['Cloud infrastructure and hosting services'],
      location: t.features_pages['UAE (Dubai)'],
      website: 'https://aws.amazon.com',
    },
    {
      name: 'Azure (الشرق الأوسط)',
      purpose: t.features_pages['AI language model services (GPT)'],
      location: t.features_pages['UAE (Dubai)'],
      website: 'https://azure.microsoft.com',
    },
    {
      name: 'G42 Cloud',
      purpose: t.features_pages['AI and cloud services'],
      location: t.features_pages['UAE (Abu Dhabi)'],
      website: 'https://g42cloud.com',
    },
    {
      name: 'Oracle Cloud (الشرق الأوسط)',
      purpose: t.features_pages['Cloud infrastructure and database services'],
      location: t.features_pages['Saudi Arabia (Jeddah)'],
      website: 'https://oracle.com/cloud',
    },
    {
      name: 'Google Cloud (الشرق الأوسط)',
      purpose: t.features_pages['Cloud infrastructure and hosting services'],
      location: t.features_pages['Saudi Arabia (Dammam)'],
      website: 'https://cloud.google.com',
    },
    {
      name: 'Cloudflare (الشرق الأوسط)',
      purpose: t.features_pages['CDN and security services'],
      location: t.features_pages['UAE (Dubai)'],
      website: 'https://cloudflare.com',
    },
    {
      name: 'Vercel (الشرق الأوسط)',
      purpose: t.features_pages['Frontend hosting and deployment'],
      location: t.features_pages['Bahrain (Manama)'],
      website: 'https://vercel.com',
    },
    {
      name: 'MongoDB Atlas (الشرق الأوسط)',
      purpose: t.features_pages['Database services'],
      location: t.features_pages['Bahrain (Manama)'],
      website: 'https://mongodb.com',
    },
  ];

  const subprocessors = isRTL ? gccSubprocessors : euSubprocessors;

  return (
    <section className="py-32">
      <div className="container max-w-5xl">
        <div className="mb-16 flex flex-col gap-6">
          <Badge variant="outline" className="w-fit">
            {t.industry['Compliance']}
          </Badge>
          <h1 className="font-serif text-4xl md:text-5xl">
            {t.features_pages['Open Subprocessors']}
          </h1>
          <p className="text-muted-foreground max-w-2xl text-base md:text-lg">
            {
              t.features_pages[
              'Know exactly where your data is and how it is being used. This page lists all third-party service providers (subprocessors) that Open uses to deliver our services.'
              ]
            }
          </p>
          <p className="text-muted-foreground text-sm">
            {t.features_pages['Current as of November 2, 2024']}
          </p>
        </div>

        {/* Subprocessors Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="pr-8 pb-4 ltr:text-left rtl:text-right text-sm font-medium">
                  {t.features_pages['Name']}
                </th>
                <th className="pr-8 pb-4 ltr:text-left rtl:text-right text-sm font-medium">
                  {t.features_pages['Purpose']}
                </th>
                <th className="pr-8 pb-4 ltr:text-left rtl:text-right text-sm font-medium">
                  {t.features_pages['Location']}
                </th>
                <th className="pb-4 ltr:text-left rtl:text-right text-sm font-medium">
                  {t.features_pages['Website']}
                </th>
              </tr>
            </thead>
            <tbody>
              {subprocessors.map((processor, idx) => (
                <tr key={idx} className="border-b last:border-0">
                  <td className="py-4 pr-8 text-sm font-medium">
                    {processor.name}
                  </td>
                  <td className="text-muted-foreground py-4 pr-8 text-sm">
                    {processor.purpose}
                  </td>
                  <td className="text-muted-foreground py-4 pr-8 text-sm">
                    {processor.location}
                  </td>
                  <td className="py-4 text-sm">
                    <a
                      href={processor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary inline-flex items-center gap-1 hover:underline"
                    >
                      {processor.website.replace('https://', '')}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Additional Information */}
        <div className="mt-16 space-y-6">
          <h2 className="text-2xl">
            {t.features_pages['Data Protection']}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
            {
              t.features_pages[
              'All subprocessors listed above are carefully selected and required to maintain appropriate security measures to protect your data. We regularly review our subprocessors to ensure they meet our security and privacy standards.'
              ]
            }
          </p>
          <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
            {
              t.features_pages[
              'This list is updated as we add or remove subprocessors. We will notify customers of any material changes in accordance with our Data Processing Agreement.'
              ]
            }
          </p>
        </div>
      </div>
    </section>
  );
};

export default SubprocessorsPage;
